import { Almanac } from "protos/robot_syncer/plant_profile";
import {
  buildBandPointGroups,
  getSampleStepMm,
  pointsToSvgPath,
} from "./formulaSamplingCalculation";
import { DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX } from "portal/utils/shootTime";
import { FORMULA_LINE_STROKE_WIDTH } from "./ShootTimeFormulaLine";
import { HOVER_HIT_WIDTH, ShootTimeXScale, ShootTimeYScale } from "./layout";
import { Tooltip, useTheme } from "@mui/material";
import React, { FunctionComponent, useMemo } from "react";

export const COMPARISON_LINE_STROKE_WIDTH = FORMULA_LINE_STROKE_WIDTH * 2;

export interface ShootTimeComparisonLineProps {
  almanac: Almanac;
  comparisonLabel?: string;
  maxRadiusMm: number;
  plotWidth: number;
  xScale: ShootTimeXScale;
  yScale: ShootTimeYScale;
}

/**
 * Carbon-default comparison curve at fine-tune index 5. One light solid
 * path, clipped by the chart: no boundary dots. Hover repeats the legend label.
 */
export const ShootTimeComparisonLine: FunctionComponent<
  ShootTimeComparisonLineProps
> = ({ almanac, comparisonLabel, maxRadiusMm, plotWidth, xScale, yScale }) => {
  const {
    palette: {
      chart: { comparisonColor },
    },
  } = useTheme();

  const pointGroups = useMemo(
    () =>
      buildBandPointGroups(
        almanac.sizeBoundariesMm,
        almanac.formulas,
        maxRadiusMm,
        getSampleStepMm(maxRadiusMm, plotWidth),
        {
          formulaVersion: "2.5",
          fineTuneIndex: DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX,
        }
      ),
    [almanac.formulas, almanac.sizeBoundariesMm, maxRadiusMm, plotWidth]
  );
  const path = pointGroups
    .map((points) => pointsToSvgPath(points, xScale, yScale))
    .filter((segment) => segment.length > 0)
    .join(" ");

  if (!path) {
    return;
  }

  const curve = (
    <g>
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={HOVER_HIT_WIDTH}
        pointerEvents="stroke"
      />
      <path
        d={path}
        fill="none"
        stroke={comparisonColor}
        strokeWidth={COMPARISON_LINE_STROKE_WIDTH}
        pointerEvents="none"
      />
    </g>
  );

  if (!comparisonLabel) {
    return curve;
  }

  return (
    <Tooltip arrow followCursor title={comparisonLabel}>
      {curve}
    </Tooltip>
  );
};
